/**
 * View-model types for the Opportunity History tab (intake). Mirrors the
 * API contract (`apps/api/src/compass/contracts/opportunity-history.ts`)
 * without importing it — packages/ui stays API-agnostic; apps/web maps the
 * contract into these shapes (see OpportunityHistoryTab).
 */

export type HistorySeverity = "major" | "minor";

export type HistoryPointVM =
  | {
      kind: "assumption";
      label: string;
      from: string | null;
      to: string | null;
      reason?: string;
    }
  | {
      kind: "revision";
      items: { severity: HistorySeverity; text: string; reason?: string }[];
    };

export interface HistoryVersionVM {
  versionNumber: number;
  /** ISO timestamp of the save that created this version. */
  createdAt: string;
  changeKind: HistorySeverity;
  /** Optional save note — surfaced when a version has no visible points. */
  note: string | null;
  authorName: string;
  badges: {
    assumptionEdits: number;
    majorRevisions: number;
    minorRevisions: number;
  };
  points: HistoryPointVM[];
}

export interface HistorySetupVM {
  createdByName: string;
  createdAt: string;
  peek: { label: string; value: string }[];
  initialAssumptions: { label: string; value: string }[];
}

export interface OpportunityHistoryVM {
  changeCount: number;
  lastChangeAt: string | null;
  contributorNames: string[];
  versions: HistoryVersionVM[];
  setup: HistorySetupVM;
}

/** Severity definitions — copy from the design exploration, verbatim. */
export const SEVERITY_TOOLTIPS: Record<HistorySeverity, string> = {
  major:
    "Major — a change that significantly revises the report, like swapping a metric, changing scope, or altering how ROI is calculated.",
  minor:
    "Minor — a small change like wording, formatting, or a definition tweak that does not materially move the ROI.",
};

export const MATERIAL_CHANGE_TOOLTIP =
  "Material change — this version changed an input that can affect ROI calculations.";
